import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Crown, Timer, Snowflake, Hourglass } from "lucide-react";
import { api } from "@/lib/api";

function formatCountdown72(totalSeconds) {
  if (totalSeconds <= 0) return { d: "00", h: "00", m: "00", s: "00" };
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return {
    d: String(d).padStart(2, "0"),
    h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"),
    s: String(s).padStart(2, "0"),
  };
}

export default function JackpotBanner() {
  const [data, setData] = useState({
    jackpot_prize_usd: 0,
    hunter_prize_usd: 0,
    total_pool_usd: 0,
    seconds_remaining: 0,
    contest_active: false,
    frozen: false,
    closing_time: null,
    duration_hours: 72,
  });
  const [localSeconds, setLocalSeconds] = useState(0);
  const [extendedFlash, setExtendedFlash] = useState(false);
  const prevClosingRef = useRef(null);

  const fetchState = async () => {
    try {
      const { data: d } = await api.get("/jackpot");
      if (
        prevClosingRef.current &&
        d.closing_time &&
        new Date(d.closing_time).getTime() > new Date(prevClosingRef.current).getTime() + 5000
      ) {
        setExtendedFlash(true);
        setTimeout(() => setExtendedFlash(false), 2500);
      }
      prevClosingRef.current = d.closing_time;
      setData(d);
      setLocalSeconds(d.seconds_remaining);
    } catch {}
  };

  useEffect(() => {
    fetchState();
    const poll = setInterval(fetchState, 3000);
    const tick = setInterval(() => setLocalSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  const { d, h, m, s } = formatCountdown72(localSeconds);
  const criticalTime = localSeconds > 0 && localSeconds < 120;
  const active = data.contest_active;

  return (
    <motion.section
      data-testid="jackpot-banner"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`relative rounded-3xl border-2 overflow-hidden mb-10 ${
        !active
          ? "border-white/15"
          : criticalTime
          ? "border-neon-red animate-glow-gold"
          : "border-neon-gold/50"
      }`}
      style={{
        background: active
          ? "radial-gradient(ellipse at 30% 20%, rgba(255,184,0,0.15), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(0,240,255,0.10), transparent 55%), #0A0A0A"
          : "#0A0A0A",
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FFB800 0 1px, transparent 1px 20px)",
        }}
        aria-hidden
      />

      {!active ? (
        <div data-testid="banner-inactive" className="relative p-8 sm:p-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground mb-4">
            <Hourglass className="w-3 h-3" />
            CONCURSO PENDIENTE
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
            Esperando el <span className="shine-text">arranque</span>
          </h2>
          <p className="text-muted-foreground text-sm max-w-lg mx-auto leading-relaxed">
            El próximo concurso de 72 horas comenzará cuando el administrador presione "Iniciar".
            Regresa pronto o suscríbete para recibir el aviso.
          </p>
        </div>
      ) : (
        <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-6 p-6 sm:p-8 lg:p-10 items-center">
          {/* JACKPOTS */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-neon-gold mb-2">
              <Crown className="w-3.5 h-3.5" />
              KING JACKPOT (50%)
              {data.frozen && (
                <span
                  data-testid="frozen-badge"
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon-cyan/15 text-neon-cyan border border-neon-cyan/40 text-[9px]"
                >
                  <Snowflake className="w-2.5 h-2.5" /> FROZEN — CLOSING
                </span>
              )}
            </div>
            <div
              data-testid="jackpot-prize"
              className="font-mono font-black tabular-nums leading-none shine-text"
              style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
            >
              ${Number(data.jackpot_prize_usd).toFixed(2)}
              <span className="text-neon-gold text-2xl sm:text-3xl ml-2 font-heading">MXN</span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs font-mono text-muted-foreground uppercase tracking-widest">
              <span>Total pool: <span data-testid="jackpot-pool" className="text-white">${Number(data.total_pool_usd).toFixed(2)}</span></span>
            </div>

            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-neon-red mb-1.5">
                <span>🎯 HUNTER JACKPOT (10%)</span>
              </div>
              <div
                data-testid="hunter-prize"
                className="font-mono font-black tabular-nums text-neon-red"
                style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}
              >
                ${Number(data.hunter_prize_usd || 0).toFixed(2)}
                <span className="text-neon-red/80 text-lg ml-2 font-heading">MXN</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                Para el que más viralice hoy
              </div>
            </div>
          </div>

          {/* COUNTDOWN 72H */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
              <Timer className="w-3.5 h-3.5" />
              Tiempo restante · 72h
              {extendedFlash && (
                <span
                  data-testid="time-extended-flash"
                  className="ml-2 px-2 py-0.5 rounded-full bg-neon-red/20 text-neon-red border border-neon-red/40 text-[9px] uppercase tracking-widest animate-pulse"
                >
                  +60s Anti-snipe
                </span>
              )}
            </div>
            <div
              data-testid="jackpot-countdown"
              className={`font-mono font-black tabular-nums leading-none ${
                criticalTime ? "text-neon-red" : "text-white"
              }`}
              style={{ fontSize: "clamp(1.5rem, 4vw, 2.75rem)" }}
            >
              <span data-testid="countdown-days">{d}</span>
              <span className="text-neon-cyan/60 text-xl ml-1 mr-3">d</span>
              <span data-testid="countdown-hours">{h}</span>
              <span className="opacity-40">:</span>
              <span data-testid="countdown-minutes">{m}</span>
              <span className="opacity-40">:</span>
              <span data-testid="countdown-seconds">{s}</span>
            </div>
            <div className="mt-3 text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
              Cualquier puja en los últimos 2 min extiende +60s
            </div>
          </div>
        </div>
      )}
    </motion.section>
  );
}
